import type { BrowserSelection } from '@/components/native-browser-view';
import type { ElementSelection } from '@/lib/element-selection';

export function browserLabel(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, '');
    return `${parsed.hostname}${path}`;
  } catch {
    return 'Browser';
  }
}

/** An element the user selected in a browser tab, as the composer's selections are. */
export function browserElementSelection(selection: BrowserSelection): ElementSelection {
  return {
    source: browserLabel(selection.url),
    location: selection.url,
    title: selection.title,
    selector: selection.selector,
    html: selection.html,
    text: selection.text,
    ...(selection.annotation ? { annotation: selection.annotation } : {}),
    rect: selection.rect,
    viewport: selection.viewport,
    ...(selection.screenshotDataUrl?.startsWith('data:image/png;base64,')
      ? { screenshot: selection.screenshotDataUrl }
      : {}),
  };
}

/** Where address-bar input that isn't an address is searched. */
const BROWSER_SEARCH_URL = 'https://duckduckgo.com/?q=';
const HAS_SCHEME = /^[a-zA-Z][a-zA-Z\d+.-]*:\/\//;
/** A host (with optional port and path): a dotted name, `localhost`, or a bracketed IPv6 address. */
const LOOKS_LIKE_HOST =
  /^(?:localhost|\[[\da-fA-F:]+\]|[^\s/?#]+\.[^\s/?#]+)(?::\d+)?(?:[/?#]\S*)?$/;
const LOCAL_HOST = /^(?:localhost|127(?:\.\d+){3}|\[::1\])(?::\d+)?(?:[/?#]|$)/;

/**
 * Turns what was typed in a browser address bar into a URL: addresses keep or
 * gain a scheme (plain HTTP for local dev servers), anything else is searched.
 */
export function resolveBrowserInput(value: string): string {
  const trimmed = value.trim();
  if (HAS_SCHEME.test(trimmed)) return trimmed;
  if (LOOKS_LIKE_HOST.test(trimmed)) {
    return `${LOCAL_HOST.test(trimmed) ? 'http' : 'https'}://${trimmed}`;
  }
  return `${BROWSER_SEARCH_URL}${encodeURIComponent(trimmed)}`;
}

/** The zoom steps of a browser tab, as Chrome has them. */
const ZOOM_FACTORS = [
  0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5,
];

/** The zoom a step in or out of `current` gives, kept within Chrome's steps. */
export function stepBrowserZoom(current: number, direction: 'in' | 'out'): number {
  if (direction === 'in') return ZOOM_FACTORS.find((factor) => factor > current + 0.001) ?? 5;
  return ZOOM_FACTORS.findLast((factor) => factor < current - 0.001) ?? 0.25;
}
